// Tiny pixel-art compositor. DOM-free so it also runs in Node for previews.
//
// A sprite is built from layers. Each layer is a grid of tone indices into its own
// palette (0 = empty). When a layer is composited it first gets a 1px dark outline
// around its silhouette, which is what gives the chunky MapleStory-style look and keeps
// overlapping parts (an arm over a hoodie, bangs over a face) readable.

export type RGB = [number, number, number];

/** Tone slots used by every layer palette. */
export const T = {
  HI: 1, // highlight
  BASE: 2,
  SH: 3, // shade
  DEEP: 4, // deep shade / inner lines
  INK: 5, // outline color, for hand-drawn inner lines
  A: 6, // accent colors, item-specific
  B: 7,
  C: 8,
  W: 9, // near-white
} as const;

export function hex(h: string): RGB {
  const n = parseInt(h.replace("#", ""), 16);
  return [n >> 16, (n >> 8) & 255, n & 255];
}

const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));

/** Mix toward a target color by k (0..1). */
export function mix(a: RGB, b: RGB, k: number): RGB {
  return [clamp(a[0] + (b[0] - a[0]) * k), clamp(a[1] + (b[1] - a[1]) * k), clamp(a[2] + (b[2] - a[2]) * k)];
}

/** A 4-tone ramp with warm highlights and cool, purple-ish shadows. */
export function ramp(base: string | RGB, accents: (string | RGB)[] = []): RGB[] {
  const b = typeof base === "string" ? hex(base) : base;
  const pal: RGB[] = [];
  pal[T.HI] = mix(b, [255, 250, 235], 0.32);
  pal[T.BASE] = b;
  pal[T.SH] = mix(mix(b, [60, 40, 110], 0.22), [0, 0, 0], 0.12);
  pal[T.DEEP] = mix(mix(b, [40, 20, 80], 0.4), [0, 0, 0], 0.22);
  pal[T.INK] = outlineOf(b);
  accents.forEach((c, i) => (pal[T.A + i] = typeof c === "string" ? hex(c) : c));
  pal[T.W] = pal[T.W] ?? [250, 248, 255];
  return pal;
}

/** Dark, slightly tinted outline so light materials don't get harsh black edges. */
export function outlineOf(b: RGB): RGB {
  return mix(mix(b, [30, 18, 40], 0.75), [0, 0, 0], 0.35);
}

interface Mark { x: number; y: number; w: number; h: number; rows: string[]; key: Record<string, number>; under: Uint8Array }

export class Layer {
  data: Uint8Array;
  marks: Mark[] = [];
  constructor(public w: number, public h: number, public pal: RGB[], public outline: RGB | null = outlineOf(pal[T.BASE])) {
    this.data = new Uint8Array(w * h);
  }
  get(x: number, y: number) {
    return x < 0 || y < 0 || x >= this.w || y >= this.h ? 0 : this.data[y * this.w + x];
  }
  set(x: number, y: number, v: number) {
    x |= 0; y |= 0;
    if (x >= 0 && y >= 0 && x < this.w && y < this.h) this.data[y * this.w + x] = v;
    return this;
  }
  rect(x: number, y: number, w: number, h: number, v: number = T.BASE) {
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) this.set(x + i, y + j, v);
    return this;
  }
  hline(x0: number, x1: number, y: number, v: number) {
    for (let x = x0; x <= x1; x++) this.set(x, y, v);
    return this;
  }
  vline(x: number, y0: number, y1: number, v: number) {
    for (let y = y0; y <= y1; y++) this.set(x, y, v);
    return this;
  }
  /** Filled ellipse inside the box [x0,x1] x [y0,y1] (inclusive). */
  ellipse(x0: number, y0: number, x1: number, y1: number, v: number = T.BASE) {
    const cx = (x0 + x1) / 2, cy = (y0 + y1) / 2, rx = (x1 - x0 + 1) / 2, ry = (y1 - y0 + 1) / 2;
    for (let y = y0; y <= y1; y++)
      for (let x = x0; x <= x1; x++) {
        const dx = (x - cx) / rx, dy = (y - cy) / ry;
        if (dx * dx + dy * dy <= 1.0) this.set(x, y, v);
      }
    return this;
  }
  /** Filled polygon, sampled at pixel centers so shapes sharing an edge tile without gaps. */
  poly(pts: [number, number][], v: number = T.BASE) {
    const ys = pts.map((p) => p[1]);
    const y0 = Math.max(0, Math.floor(Math.min(...ys))), y1 = Math.min(this.h - 1, Math.ceil(Math.max(...ys)));
    for (let y = y0; y <= y1; y++) {
      const cy = y + 0.5, xs: number[] = [];
      for (let i = 0; i < pts.length; i++) {
        const [ax, ay] = pts[i], [bx, by] = pts[(i + 1) % pts.length];
        if ((ay <= cy && by > cy) || (by <= cy && ay > cy)) xs.push(ax + ((cy - ay) * (bx - ax)) / (by - ay));
      }
      xs.sort((a, b) => a - b);
      for (let k = 0; k + 1 < xs.length; k += 2)
        for (let x = Math.ceil(xs[k] - 0.5); x <= Math.floor(xs[k + 1] - 0.5); x++) this.set(x, y, v);
    }
    return this;
  }
  /** Rows given as [y, x0, x1] spans. */
  spans(rows: [number, number, number][], v: number = T.BASE) {
    for (const [y, a, b] of rows) this.hline(a, b, y, v);
    return this;
  }
  /**
   * Stamps an ASCII template. Each char maps through `key` to a tone; '.' and ' ' skip.
   * `flip` mirrors it horizontally around its own width.
   */
  stamp(x: number, y: number, rows: string[], key: Record<string, number>, flip = false) {
    rows.forEach((row, j) => {
      for (let i = 0; i < row.length; i++) {
        const ch = row[flip ? row.length - 1 - i : i];
        if (ch === "." || ch === " ") continue;
        const v = key[ch];
        if (v === undefined) continue;
        this.set(x + i, y + j, v);
      }
    });
    return this;
  }
  /**
   * Stamps a one-sided print (letters, logos) that must never read backwards. When the layer
   * is mirrored for a left-facing sprite the print moves to the other side but keeps its own
   * orientation. Draw marks last: anything drawn over a mark's box afterwards is lost on mirror.
   */
  mark(x: number, y: number, rows: string[], key: Record<string, number>) {
    const w = Math.max(...rows.map((r) => r.length)), h = rows.length, under = new Uint8Array(w * h);
    for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) under[j * w + i] = this.get(x + i, y + j);
    this.marks.push({ x, y, w, h, rows, key, under });
    return this.stamp(x, y, rows, key);
  }
  /** A horizontally mirrored copy; marks are re-stamped the right way round. */
  mirrored(): Layer {
    const M = new Layer(this.w, this.h, this.pal, this.outline);
    for (let y = 0; y < this.h; y++)
      for (let x = 0; x < this.w; x++) M.data[y * this.w + (this.w - 1 - x)] = this.data[y * this.w + x];
    for (const m of this.marks) {
      const mx = this.w - m.x - m.w, under = new Uint8Array(m.under.length);
      for (let j = 0; j < m.h; j++) for (let i = 0; i < m.w; i++) under[j * m.w + i] = m.under[j * m.w + (m.w - 1 - i)];
      for (let j = 0; j < m.h; j++) for (let i = 0; i < m.w; i++) M.set(mx + i, m.y + j, under[j * m.w + i]);
      M.stamp(mx, m.y, m.rows, m.key);
      M.marks.push({ ...m, x: mx, under });
    }
    return M;
  }
  /** Clears pixels (makes a hole). */
  erase(x: number, y: number, w: number, h: number) {
    return this.rect(x, y, w, h, 0);
  }
  /** Keeps only pixels that are also set in `mask`. */
  clipTo(mask: Layer) {
    for (let i = 0; i < this.data.length; i++) if (!mask.data[i]) this.data[i] = 0;
    return this;
  }
  /**
   * Soft auto-shading for BASE pixels: a shade band along the right and bottom edges
   * (light comes from the top-left) and a 1px highlight on the upper-left rim.
   */
  shade(opts: { right?: number; bottom?: number; hi?: boolean } = {}) {
    const right = opts.right ?? 2, bottom = opts.bottom ?? 1, hi = opts.hi ?? true;
    const src = this.data.slice();
    const g = (x: number, y: number) => (x < 0 || y < 0 || x >= this.w || y >= this.h ? 0 : src[y * this.w + x]);
    for (let y = 0; y < this.h; y++)
      for (let x = 0; x < this.w; x++) {
        if (src[y * this.w + x] !== T.BASE) continue;
        let v: number = T.BASE;
        for (let k = 1; k <= right; k++) if (!g(x + k, y)) v = T.SH;
        for (let k = 1; k <= bottom; k++) if (!g(x, y + k)) v = T.SH;
        if (v === T.BASE && hi && !g(x - 1, y - 1) && (!g(x - 1, y) || !g(x, y - 1)) && g(x + 1, y + 1)) v = T.HI;
        this.data[y * this.w + x] = v;
      }
    return this;
  }
}

/** Smallest box holding every opaque pixel (null when empty). */
export function bounds(b: Bitmap): { x: number; y: number; w: number; h: number } | null {
  let x0 = b.w, y0 = b.h, x1 = -1, y1 = -1;
  for (let y = 0; y < b.h; y++)
    for (let x = 0; x < b.w; x++)
      if (b.data[(y * b.w + x) * 4 + 3]) { if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
  return x1 < 0 ? null : { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

export function crop(b: Bitmap, r: { x: number; y: number; w: number; h: number }): Bitmap {
  const out = new Uint8ClampedArray(r.w * r.h * 4);
  for (let y = 0; y < r.h; y++) out.set(b.data.subarray(((r.y + y) * b.w + r.x) * 4, ((r.y + y) * b.w + r.x + r.w) * 4), y * r.w * 4);
  return { w: r.w, h: r.h, data: out };
}

export interface Bitmap {
  w: number;
  h: number;
  data: Uint8ClampedArray; // RGBA
}

/** Composites layers bottom to top, each with its own outline. */
export function composite(w: number, h: number, layers: (Layer | null | undefined)[]): Bitmap {
  const out = new Uint8ClampedArray(w * h * 4);
  const put = (x: number, y: number, c: RGB) => {
    const o = (y * w + x) * 4;
    out[o] = c[0]; out[o + 1] = c[1]; out[o + 2] = c[2]; out[o + 3] = 255;
  };
  for (const L of layers) {
    if (!L) continue;
    if (L.outline) {
      for (let y = 0; y < h; y++)
        for (let x = 0; x < w; x++) {
          if (L.get(x, y)) continue;
          if (L.get(x - 1, y) || L.get(x + 1, y) || L.get(x, y - 1) || L.get(x, y + 1)) put(x, y, L.outline);
        }
    }
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        const v = L.get(x, y);
        if (v) put(x, y, L.pal[v] ?? L.pal[T.BASE]);
      }
  }
  return { w, h, data: out };
}

/** Mirrors a bitmap horizontally. */
export function flipX(b: Bitmap): Bitmap {
  const out = new Uint8ClampedArray(b.data.length);
  for (let y = 0; y < b.h; y++)
    for (let x = 0; x < b.w; x++) {
      const s = (y * b.w + x) * 4, d = (y * b.w + (b.w - 1 - x)) * 4;
      out[d] = b.data[s]; out[d + 1] = b.data[s + 1]; out[d + 2] = b.data[s + 2]; out[d + 3] = b.data[s + 3];
    }
  return { w: b.w, h: b.h, data: out };
}
